import { numberToWordsIndian } from "@project/lib/receipt-words";

export interface BhojanCategory {
  id: number;
  name: string;
  /** Booking cut-off time, e.g. "11:00" or "11:00:00" */
  cut_off?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type BhojanCategoryPayload = Pick<
  BhojanCategory,
  "name" | "cut_off" | "is_active"
>;

export const BHOJAN_SPONSORSHIP_TYPES = ["other", "special"] as const;

export type BhojanSponsorshipType = (typeof BHOJAN_SPONSORSHIP_TYPES)[number];

export const BHOJAN_SPONSORSHIP_TYPE_OPTIONS: {
  value: BhojanSponsorshipType;
  label: string;
}[] = [
  { value: "other", label: "Other Meal Sponsorship" },
  { value: "special", label: "Special Service Sponsorships" },
];

export function getBhojanSponsorshipTypeLabel(value?: string | null): string {
  const hit = BHOJAN_SPONSORSHIP_TYPE_OPTIONS.find(
    option => option.value === value
  );
  return hit?.label ?? value?.trim() ?? "—";
}

/** Normalize API / legacy values to `other` | `special`. */
export function normalizeBhojanSponsorshipType(
  value?: string | null
): BhojanSponsorshipType | null {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!raw) {
    return null;
  }
  if (raw === "other" || raw === "other meal sponsorship") {
    return "other";
  }
  if (raw === "special" || raw === "special service sponsorships") {
    return "special";
  }
  return null;
}

export interface BhojanPackage {
  id: number;
  category: number;
  category_name?: string;
  /** Booking cut-off from category, e.g. "16:00:00" (Asia/Kolkata) */
  cut_off?: string | null;
  name: string;
  description?: string;
  price: string;
  /** Section on public Bhojanshala page */
  sponsorships_type?: BhojanSponsorshipType | string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type BhojanPackagePayload = Pick<
  BhojanPackage,
  | "category"
  | "name"
  | "description"
  | "price"
  | "sponsorships_type"
  | "is_active"
>;

export interface BhojanScheme {
  id: number;
  badge_text: string;
  title: string;
  technical_name: string;
  price: string;
  sum_of_rupees: string;
  features: string[];
  display_order: number;
  is_highlighted: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type BhojanSchemePayload = Pick<
  BhojanScheme,
  | "badge_text"
  | "title"
  | "technical_name"
  | "price"
  | "sum_of_rupees"
  | "features"
  | "display_order"
  | "is_highlighted"
  | "is_active"
>;

export const BHOJAN_DONATION_STATUS_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export type BhojanDonationStatus =
  (typeof BHOJAN_DONATION_STATUS_OPTIONS)[number]["value"];

export interface BhojanDonationPayment {
  id: number;
  razorpay_order_id?: string;
  razorpay_payment_id?: string | null;
  amount: string;
  currency?: string;
  status?: string;
  payment_method?: string | null;
  payment_method_details?: unknown;
  receipt_no?: string | null;
  created_at?: string;
}

export interface BhojanDonation {
  id: number;
  bhojan?: number | string | null;
  bhojan_id?: number | null;
  bhojan_name?: string | null;
  bhojan_price?: string | number | null;
  package_name?: string | null;
  scheme?: number | string | null;
  scheme_id?: number | null;
  scheme_title?: string | null;
  scheme_name?: string | null;
  category?: number | string | null;
  category_id?: number | null;
  category_name?: string | null;
  preferred_sponsorship_date?: string | null;
  donor_name?: string | null;
  name?: string | null;
  mobile_number?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  city?: string | null;
  full_address?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  occasion?: string | null;
  preferred_tithi?: string | null;
  display_name?: string | null;
  special_instructions?: string | null;
  message?: string | null;
  is_anonymous?: boolean;
  info_confirmed?: boolean;
  contact_consent?: boolean;
  amount?: string | number | null;
  price?: string | number | null;
  payment_mode?: string | null;
  mode?: string | null;
  status?: string | null;
  payment_status?: string | null;
  receipt_no?: string | null;
  receipt_number?: string | null;
  payments?: BhojanDonationPayment[];
  created_at?: string;
  updated_at?: string;
  donation_date?: string;
}

const BHOJAN_PAYMENT_MODE_LABELS: Record<string, string> = {
  neft: "NEFT",
  rtgs: "RTGS",
  imps: "IMPS",
  upi: "UPI",
  cheque: "Cheque",
  cash: "Cash",
  wallet: "Wallet",
  card: "Card",
  netbanking: "Netbanking",
  online: "Online",
  offline: "Offline",
};

export function getBhojanDonationDonorName(
  row: Pick<
    BhojanDonation,
    "donor_name" | "name" | "display_name" | "is_anonymous"
  >
): string {
  if (row.is_anonymous) {
    return "Anonymous";
  }
  return (
    row.donor_name?.trim() ??
    row.name?.trim() ??
    row.display_name?.trim() ??
    "—"
  );
}

export function getBhojanDonationPhone(
  row: Pick<BhojanDonation, "mobile_number" | "phone" | "mobile">
): string {
  return (
    row.mobile_number?.trim() ?? row.phone?.trim() ?? row.mobile?.trim() ?? "—"
  );
}

export function getBhojanDonationPackageLabel(
  row: Pick<
    BhojanDonation,
    "bhojan_name" | "package_name" | "scheme_title" | "scheme_name"
  >
): string {
  return (
    row.bhojan_name?.trim() ??
    row.package_name?.trim() ??
    row.scheme_title?.trim() ??
    row.scheme_name?.trim() ??
    "—"
  );
}

export function getBhojanDonationAmount(
  row: Pick<BhojanDonation, "amount" | "price" | "bhojan_price">
): number {
  return Number(row.amount ?? row.price ?? row.bhojan_price ?? 0);
}

export function getBhojanDonationStatus(row: BhojanDonation): string {
  if (row.payments?.length) {
    return row.payments[0].status ?? row.status ?? row.payment_status ?? "—";
  }
  return row.status ?? row.payment_status ?? "—";
}

export function getBhojanDonationReceiptNo(row: BhojanDonation): string {
  const payment = row.payments?.[0];
  return String(
    row.receipt_no ??
      row.receipt_number ??
      payment?.receipt_no ??
      payment?.razorpay_payment_id ??
      (row.id ? `BHJ-${row.id}` : "—")
  );
}

export function getBhojanDonationPaymentMode(row: BhojanDonation): string {
  const raw =
    row.payment_mode?.trim() ?? row.payments?.[0]?.payment_method?.trim() ?? "";
  if (!raw) {
    return "—";
  }
  const key = raw.toLowerCase();
  return (
    BHOJAN_PAYMENT_MODE_LABELS[key] ??
    raw.charAt(0).toUpperCase() + raw.slice(1)
  );
}

export function buildBhojanDonationAddress(row: BhojanDonation): string {
  return [row.full_address, row.city, row.state, row.pincode, row.country]
    .map(part => (part !== null ? String(part).trim() : ""))
    .filter(Boolean)
    .join(", ");
}

export function normalizeBhojanDonation(row: BhojanDonation): BhojanDonation {
  return {
    ...row,
    donor_name: row.donor_name ?? row.name ?? undefined,
    phone: row.mobile_number ?? row.phone ?? row.mobile ?? undefined,
    mobile_number: row.mobile_number ?? row.phone ?? row.mobile ?? undefined,
    amount: row.amount ?? row.price ?? row.bhojan_price,
    package_name: row.package_name ?? row.bhojan_name,
    bhojan_name: row.bhojan_name ?? row.package_name,
    scheme_title: row.scheme_title ?? row.scheme_name,
    receipt_no: getBhojanDonationReceiptNo(row),
    status: getBhojanDonationStatus(row),
    payment_mode: (() => {
      const label = getBhojanDonationPaymentMode(row);
      return label === "—" ? undefined : label;
    })(),
  };
}

export function formatBhojanPrice(value?: string | number | null): string {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) {
    return "—";
  }
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function toBhojanPrice(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) {
    throw new Error("Enter a valid price.");
  }
  return num.toFixed(2);
}

/** Default lunch cut-off when API does not send one. */
export const DEFAULT_BHOJAN_CUT_OFF = "11:00:00";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function toIsoDateLocal(year: number, month: number, day: number) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Parse "16:00:00", "16:00", or "4:00 PM" → minutes since midnight. */
export function parseBhojanCutOffMinutes(
  cutOff?: string | null,
  fallback = DEFAULT_BHOJAN_CUT_OFF
): number {
  const raw = String(cutOff ?? fallback).trim();
  if (!raw) {
    return parseBhojanCutOffMinutes(fallback, "11:00:00");
  }

  const ampm = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (ampm) {
    let hours = Number(ampm[1]);
    const minutes = Number(ampm[2]);
    const period = ampm[4].toUpperCase();
    if (period === "PM" && hours < 12) {
      hours += 12;
    }
    if (period === "AM" && hours === 12) {
      hours = 0;
    }
    return hours * 60 + minutes;
  }

  const parts = raw.split(":");
  const hours = Number(parts[0]);
  const minutes = Number(parts[1] ?? 0);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return parseBhojanCutOffMinutes(fallback, "11:00:00");
  }
  return hours * 60 + minutes;
}

/** "16:00:00" → "4:00 PM" for UI. */
export function formatBhojanCutOffLabel(
  cutOff?: string | null,
  fallback = DEFAULT_BHOJAN_CUT_OFF
): string {
  const total = parseBhojanCutOffMinutes(cutOff, fallback);
  const hours24 = Math.floor(total / 60) % 24;
  const minutes = total % 60;
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${pad2(minutes)} ${period}`;
}

function getKolkataDateTimeParts(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value ?? 0);

  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") === 24 ? 0 : get("hour"),
    minute: get("minute"),
  };
}

/** True when current Asia/Kolkata time is at or past the cut-off. */
export function isBhojanBookingWindowClosed(
  cutOff?: string | null,
  now = new Date()
): boolean {
  const kolkata = getKolkataDateTimeParts(now);
  const nowMinutes = kolkata.hour * 60 + kolkata.minute;
  return nowMinutes >= parseBhojanCutOffMinutes(cutOff);
}

/** Today's calendar date in Asia/Kolkata (YYYY-MM-DD). */
export function getBhojanTodayInKolkata(now = new Date()): string {
  const { year, month, day } = getKolkataDateTimeParts(now);
  return toIsoDateLocal(year, month, day);
}

/**
 * Earliest bookable date for a package in Asia/Kolkata.
 * Before cut-off → today; at/after cut-off → tomorrow.
 */
export function getBhojanEarliestPackageDate(
  cutOff?: string | null,
  now = new Date()
): string {
  const { year, month, day } = getKolkataDateTimeParts(now);
  const date = new Date(year, month - 1, day);
  if (isBhojanBookingWindowClosed(cutOff, now)) {
    date.setDate(date.getDate() + 1);
  }
  return toIsoDateLocal(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate()
  );
}

/** e.g. 51000 → "Indian Rupees Fifty One Thousand Only" */
export function priceToSumOfRupees(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const num = Math.floor(Number(value));
  if (!Number.isFinite(num) || num < 0) {
    return "";
  }
  const words = numberToWordsIndian(num)
    .replace(/\s*Rupees Only$/i, "")
    .trim();
  return words ? `Indian Rupees ${words} Only` : "";
}

export function toBhojanFeatures(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(/\n|,/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
}

export const BHOJAN_OCCASION_OPTIONS = [
  "Birthday",
  "Anniversary",
  "Memorial",
  "Festival",
  "Punya Tithi",
  "Other",
] as const;

export type BhojanOccasion = (typeof BHOJAN_OCCASION_OPTIONS)[number];

export interface BhojanCreateOrderPayload {
  bhojan_id?: number;
  scheme_id?: number;
  preferred_sponsorship_date: string;
  preferred_tithi?: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion?: string;
  display_name?: string;
  special_instructions?: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  mode: "online";
}

/** Admin offline add — POST /bhojan-donations/add/ */
export interface BhojanAddDonationPayload {
  bhojan_id: number;
  preferred_sponsorship_date: string;
  preferred_tithi?: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion?: string;
  display_name?: string;
  special_instructions?: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  amount: string;
  mode: "offline";
  payment_mode: string;
  payment_reference?: string;
}

export const BHOJAN_OFFLINE_PAYMENT_MODE_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
] as const;

export interface BhojanCreateOrderResult {
  order_id: string;
  amount: number;
  currency: string;
  key_id?: string;
  donation_id?: number;
}

export interface BhojanVerifyPaymentPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface BhojanVerifyPaymentResult {
  receipt_no?: string;
  message?: string;
}

export type BhojanSponsorshipSelection =
  | {
      kind: "package";
      id: number;
      title: string;
      description?: string;
      price: string;
      categoryLabel: string;
      servicesLabel?: string;
      packageType?: string;
      /** From package/category API, e.g. "16:00:00" */
      cutOff?: string | null;
    }
  | {
      kind: "scheme";
      id: number;
      title: string;
      description?: string;
      price: string;
      categoryLabel: string;
      badgeText?: string;
      technicalName?: string;
    }
  | {
      kind: "supporting";
      title: string;
      description?: string;
      price: string;
      categoryLabel: string;
    };

export function parseBhojanCreateOrderResponse(
  response: unknown
): BhojanCreateOrderResult {
  const root = response as {
    order_id?: string;
    amount?: number;
    currency?: string;
    key_id?: string;
    donation_id?: number;
    data?: {
      order_id?: string;
      amount?: number;
      currency?: string;
      key_id?: string;
      donation_id?: number;
    };
  };

  if (root.order_id) {
    return {
      order_id: root.order_id,
      amount: root.amount ?? 0,
      currency: root.currency ?? "INR",
      key_id: root.key_id,
      donation_id: root.donation_id,
    };
  }

  return {
    order_id: root.data?.order_id ?? "",
    amount: root.data?.amount ?? 0,
    currency: root.data?.currency ?? "INR",
    key_id: root.data?.key_id,
    donation_id: root.data?.donation_id,
  };
}

export function parseBhojanVerifyPaymentResponse(
  response: unknown
): BhojanVerifyPaymentResult {
  const root = response as {
    receipt_no?: string;
    message?: string;
    data?: { receipt_no?: string; receipt_number?: string; message?: string };
  };
  return {
    receipt_no:
      root.receipt_no ?? root.data?.receipt_no ?? root.data?.receipt_number,
    message: root.message ?? root.data?.message,
  };
}
